import React, { useEffect, useMemo, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend,
  AreaChart, Area, ScatterChart, Scatter, ZAxis, Cell,
} from 'recharts';
import {
  Loader2, ArrowLeft, Users, CheckCircle2, Clock, FileSpreadsheet, Filter, X,
  BarChart2, Sparkles, List, TableProperties, Lock, QrCode, Wallet, Coins, Check,
  Download, Globe, Tag, Code2, Rocket, Link as LinkIcon,
} from 'lucide-react';
import ReactMarkdown from 'react-markdown';
import { Survey, SurveyQuestionDraft } from '../types';
import { fetchSurveyById, fetchSurveyQuestions, analyzeSurveys } from '../services/surveyService';
import {
  DashboardSummary, QuestionStat, SegmentFilter, getDashboardSummary, getQuestionStats,
  getCrosstab, getNumericPairs, pivotCrosstab, valueLabel, getText, NumericPairs,
} from '../services/analyticsService';
import { chiSquareTest, pearsonFromSums, significance, effectStrength } from '../services/stats';
import {
  DashboardOffer, DashboardPurchase, PublishConfig, ReleaseFormat, getDashboardOffer, startKhqrPurchase,
  payDashboardWithX402, listDashboardSales, confirmDashboardPurchase, publishDashboard, unpublishDashboard,
  getDashboardJson,
} from '../services/dashboardSaleService';
import { buildKhqr } from '../services/khqr';
import { hasWallet } from '../services/x402Client';

const COLORS = ['#6366F1', '#10B981', '#F59E0B', '#EF4444', '#8B5CF6', '#06B6D4', '#EC4899', '#84CC16', '#F97316', '#14B8A6', '#A855F7', '#0EA5E9'];
const CHOICE = ['MULTIPLE_CHOICE', 'DROPDOWN', 'CHECKBOX', 'RANKING'];

const fmtDuration = (s: number | null) => {
  if (!s) return '—';
  if (s < 60) return `${s}s`;
  if (s < 3600) return `${Math.round(s / 60)}m`;
  return `${(s / 3600).toFixed(1)}h`;
};

const SurveyDashboardView: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const [survey, setSurvey] = useState<Survey | null>(null);
  const [questions, setQuestions] = useState<SurveyQuestionDraft[]>([]);
  const [summary, setSummary] = useState<DashboardSummary | null>(null);
  const [baseStats, setBaseStats] = useState<QuestionStat[]>([]); // unfiltered (for filter options)
  const [stats, setStats] = useState<QuestionStat[]>([]);
  const [filter, setFilter] = useState<SegmentFilter | null>(null);
  const [loading, setLoading] = useState(true);
  const [statsLoading, setStatsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [offer, setOffer] = useState<DashboardOffer | null>(null);
  const [gate, setGate] = useState<'loading' | 'paywall' | 'open' | 'private'>('loading');

  // initial load: check access (owner / purchased) before pulling analytics.
  useEffect(() => { if (id) init(id); }, [id]);
  const init = async (sid: string) => {
    setLoading(true); setError(null);
    try {
      const o = await getDashboardOffer(sid);
      setOffer(o);
      const accessible = o.is_owner || o.already_paid || (o.published && o.access === 'FREE');
      if (accessible) { setGate('open'); await load(sid); }
      else if (o.published && o.access === 'PAID') { setGate('paywall'); }
      else { setGate('private'); }
    } catch (e: any) {
      setError(e.message || 'Failed to load dashboard.');
    } finally { setLoading(false); }
  };
  const load = async (sid: string) => {
    const [s, qs, sum, st] = await Promise.all([
      fetchSurveyById(sid), fetchSurveyQuestions(sid), getDashboardSummary(sid), getQuestionStats(sid, null),
    ]);
    setSurvey(s); setQuestions(qs); setSummary(sum); setBaseStats(st); setStats(st);
  };

  // reload per-question stats when the segment filter changes
  useEffect(() => {
    if (!id || gate !== 'open') return;
    (async () => {
      setStatsLoading(true);
      try { setStats(await getQuestionStats(id, filter)); }
      catch (e) { console.error(e); }
      finally { setStatsLoading(false); }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filter]);

  const configById = useMemo(() => {
    const m = new Map<string, any>();
    questions.forEach((q) => m.set(q.id, q.config));
    return m;
  }, [questions]);

  // Filterable (categorical) questions + their option labels, from the unfiltered base.
  const filterQuestions = useMemo(
    () => baseStats.filter((s) => CHOICE.includes(s.question_type) && (s.counts?.length || 0) > 0),
    [baseStats]
  );

  if (loading) return <div className="flex justify-center py-16"><Loader2 className="animate-spin text-indigo-500" size={32} /></div>;
  if (error) return (
    <div className="bg-red-900/20 border border-red-500/30 p-4 rounded-xl text-red-200 text-sm flex items-start gap-2">
      <X size={16} className="text-red-400 mt-0.5" /> {error}
    </div>
  );
  if (gate === 'private') return (
    <div className="max-w-md mx-auto bg-slate-800 border border-slate-700 rounded-2xl p-8 text-center mt-10">
      <Lock size={40} className="text-slate-500 mx-auto mb-3" />
      <h2 className="text-white font-bold text-lg">Private dashboard</h2>
      <p className="text-slate-400 text-sm mt-1">This survey's analytics are not available.</p>
    </div>
  );
  if (gate === 'paywall' && offer) return <Paywall offer={offer} onUnlocked={() => init(id!)} />;

  return (
    <div className="space-y-6 animate-in fade-in duration-300 pb-12">
      {/* Header + tabs */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-3 border-b border-slate-700 pb-4">
        <div className="flex items-center gap-3">
          <button onClick={() => navigate('/research/campaigns')} className="p-2 hover:bg-slate-800 rounded-lg text-slate-400 hover:text-white"><ArrowLeft size={20} /></button>
          <div>
            <h1 className="text-xl font-bold text-white">{survey ? getText(survey.title) : 'Dashboard'}</h1>
            <p className="text-slate-400 text-sm">Analytics dashboard</p>
          </div>
        </div>
        <div className="flex items-center gap-2 self-start">
          {(offer?.is_owner || offer?.formats?.includes('JSON')) && <JsonButton surveyId={id!} />}
          <div className="flex items-center gap-1 bg-slate-800 rounded-lg p-1">
            <span className="px-3 py-1.5 rounded-md text-sm font-medium bg-indigo-600 text-white flex items-center gap-1.5"><BarChart2 size={15} /> Dashboard</span>
            {offer?.is_owner && <button onClick={() => navigate(`/research/campaigns/${id}/responses`)} className="px-3 py-1.5 rounded-md text-sm font-medium text-slate-400 hover:text-white flex items-center gap-1.5"><List size={15} /> Responses</button>}
          </div>
        </div>
      </div>

      {/* Release panel (owner only) */}
      {offer?.is_owner && <ReleasePanel offer={offer} onChanged={() => init(id!)} />}

      {/* KPI cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <Kpi icon={<Users size={18} />} label="Responses" value={summary?.total ?? 0} />
        <Kpi icon={<CheckCircle2 size={18} />} label="Completion" value={`${summary?.completion_rate ?? 0}%`} />
        <Kpi icon={<Clock size={18} />} label="Avg. time" value={fmtDuration(summary?.avg_duration_seconds ?? null)} />
        <Kpi icon={<FileSpreadsheet size={18} />} label="Imported" value={summary?.imported ?? 0} accent={(summary?.imported ?? 0) > 0 ? 'sky' : undefined} />
      </div>

      {/* Responses over time */}
      {summary && summary.by_day.length > 0 && (
        <Card title="Responses over time">
          <ResponsiveContainer width="100%" height={220}>
            <AreaChart data={summary.by_day}>
              <defs>
                <linearGradient id="respGrad" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#6366F1" stopOpacity={0.5} />
                  <stop offset="100%" stopColor="#6366F1" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#334155" />
              <XAxis dataKey="day" stroke="#94a3b8" fontSize={11} tickLine={false} />
              <YAxis stroke="#94a3b8" fontSize={11} tickLine={false} allowDecimals={false} />
              <Tooltip contentStyle={tooltipStyle} />
              <Area type="monotone" dataKey="count" stroke="#6366F1" fill="url(#respGrad)" strokeWidth={2} />
            </AreaChart>
          </ResponsiveContainer>
        </Card>
      )}

      {/* Segment filter */}
      <SegmentBar
        filterQuestions={filterQuestions}
        filter={filter}
        setFilter={setFilter}
        configById={configById}
        statsLoading={statsLoading}
      />

      {/* Per-question cards */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        {stats.map((st) => <QuestionCard key={st.question_id} stat={st} />)}
      </div>

      {/* Cross-tab + tests */}
      <CrossTabPanel surveyId={id!} questions={questions} configById={configById} filter={filter} />

      {/* Sales (owner only) */}
      {offer?.is_owner && offer.for_sale && <OwnerSales surveyId={id!} />}

      {/* AI insights */}
      <AiInsights surveyId={id!} />
    </div>
  );
};

// ── KPI / Card primitives ────────────────────────────────────────────────────
const tooltipStyle = { backgroundColor: '#1e293b', border: '1px solid #334155', borderRadius: 8, color: '#fff', fontSize: 12 } as const;

const Kpi: React.FC<{ icon: React.ReactNode; label: string; value: any; accent?: 'sky' }> = ({ icon, label, value, accent }) => (
  <div className="bg-slate-800 border border-slate-700 rounded-xl p-4">
    <div className={`flex items-center gap-2 text-xs font-medium ${accent === 'sky' ? 'text-sky-300' : 'text-slate-400'}`}>{icon} {label}</div>
    <div className="text-2xl font-bold text-white mt-1">{value}</div>
  </div>
);

const Card: React.FC<{ title: string; children: React.ReactNode; subtitle?: React.ReactNode }> = ({ title, children, subtitle }) => (
  <div className="bg-slate-800 border border-slate-700 rounded-xl p-5">
    <div className="mb-3">
      <h3 className="text-white font-semibold">{title}</h3>
      {subtitle && <div className="text-xs text-slate-400 mt-0.5">{subtitle}</div>}
    </div>
    {children}
  </div>
);

// ── Segment filter bar ───────────────────────────────────────────────────────
const SegmentBar: React.FC<{
  filterQuestions: QuestionStat[]; filter: SegmentFilter | null;
  setFilter: (f: SegmentFilter | null) => void; configById: Map<string, any>; statsLoading: boolean;
}> = ({ filterQuestions, filter, setFilter, configById, statsLoading }) => {
  const [qid, setQid] = useState('');
  const selected = filterQuestions.find((q) => q.question_id === qid);
  if (filterQuestions.length === 0) return null;
  return (
    <div className="bg-slate-800/60 border border-slate-700 rounded-xl p-3 flex flex-wrap items-center gap-2">
      <span className="text-slate-300 text-sm font-medium flex items-center gap-1.5"><Filter size={15} /> Segment:</span>
      <select value={qid} onChange={(e) => setQid(e.target.value)}
        className="bg-slate-900 border border-slate-600 rounded-lg px-2 py-1.5 text-white text-sm focus:outline-none focus:border-indigo-500">
        <option value="">Choose a question…</option>
        {filterQuestions.map((q) => <option key={q.question_id} value={q.question_id}>{getText(q.question_text)}</option>)}
      </select>
      {selected && (
        <select value={filter?.question_id === qid ? filter.value : ''}
          onChange={(e) => setFilter(e.target.value ? { question_id: qid, value: e.target.value } : null)}
          className="bg-slate-900 border border-slate-600 rounded-lg px-2 py-1.5 text-white text-sm focus:outline-none focus:border-indigo-500">
          <option value="">All values</option>
          {selected.counts?.map((c) => (
            <option key={String(c.value)} value={String(c.value)}>{valueLabel(configById.get(selected.question_id), c.value)} ({c.count})</option>
          ))}
        </select>
      )}
      {statsLoading && <Loader2 size={15} className="animate-spin text-indigo-400" />}
      {filter && (
        <button onClick={() => { setFilter(null); setQid(''); }} className="ml-auto text-xs text-slate-400 hover:text-white flex items-center gap-1"><X size={13} /> Clear</button>
      )}
    </div>
  );
};

// ── Per-question card ────────────────────────────────────────────────────────
const QuestionCard: React.FC<{ stat: QuestionStat }> = ({ stat }) => {
  const title = `${stat.order_index}. ${getText(stat.question_text)}`;
  const subtitle = `${stat.question_type.replace('_', ' ').toLowerCase()} · n=${stat.n}${stat.multi ? ' (multi-select)' : ''}`;

  if (CHOICE.includes(stat.question_type)) {
    const data = (stat.counts || []).map((c) => ({ name: valueLabel(stat.config, c.value), count: c.count, pct: stat.n ? Math.round((c.count / stat.n) * 100) : 0 })).slice(0, 12);
    return (
      <Card title={title} subtitle={subtitle}>
        {data.length === 0 ? <Empty /> : (
          <>
            <ResponsiveContainer width="100%" height={Math.max(140, data.length * 34)}>
              <BarChart data={data} layout="vertical" margin={{ left: 8, right: 16 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#334155" horizontal={false} />
                <XAxis type="number" stroke="#94a3b8" fontSize={11} allowDecimals={false} />
                <YAxis type="category" dataKey="name" stroke="#94a3b8" fontSize={11} width={120} tick={{ width: 115 }} />
                <Tooltip contentStyle={tooltipStyle} formatter={(v: any, _n, p: any) => [`${v} (${p.payload.pct}%)`, 'count']} />
                <Bar dataKey="count" radius={[0, 4, 4, 0]}>
                  {data.map((_d, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </>
        )}
      </Card>
    );
  }

  if (stat.question_type === 'RATING') {
    const data = (stat.histogram || []).map((h) => ({ name: String(h.value), count: h.count }));
    return (
      <Card title={title} subtitle={subtitle}>
        <div className="grid grid-cols-3 sm:grid-cols-6 gap-2 mb-3 text-center">
          {[['mean', stat.mean], ['median', stat.median], ['mode', stat.mode], ['std', stat.stddev], ['min', stat.min], ['max', stat.max]].map(([k, v]) => (
            <div key={k as string} className="bg-slate-900/50 rounded-lg py-1.5">
              <div className="text-[10px] uppercase text-slate-500">{k as string}</div>
              <div className="text-sm font-bold text-white">{v ?? '—'}</div>
            </div>
          ))}
        </div>
        {data.length === 0 ? <Empty /> : (
          <ResponsiveContainer width="100%" height={180}>
            <BarChart data={data}>
              <CartesianGrid strokeDasharray="3 3" stroke="#334155" />
              <XAxis dataKey="name" stroke="#94a3b8" fontSize={11} />
              <YAxis stroke="#94a3b8" fontSize={11} allowDecimals={false} />
              <Tooltip contentStyle={tooltipStyle} />
              <Bar dataKey="count" fill="#6366F1" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        )}
      </Card>
    );
  }

  if (stat.question_type === 'DATE') {
    const data = (stat.histogram || []).map((h) => ({ name: String(h.value), count: h.count }));
    return (
      <Card title={title} subtitle={subtitle}>
        {data.length === 0 ? <Empty /> : (
          <ResponsiveContainer width="100%" height={180}>
            <BarChart data={data}>
              <CartesianGrid strokeDasharray="3 3" stroke="#334155" />
              <XAxis dataKey="name" stroke="#94a3b8" fontSize={11} />
              <YAxis stroke="#94a3b8" fontSize={11} allowDecimals={false} />
              <Tooltip contentStyle={tooltipStyle} />
              <Bar dataKey="count" fill="#10B981" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        )}
      </Card>
    );
  }

  // TEXT (and fallback): top words + samples
  const words = (stat.top_words || []).map((w) => ({ name: String(w.value), count: w.count }));
  return (
    <Card title={title} subtitle={subtitle}>
      {words.length > 0 && (
        <ResponsiveContainer width="100%" height={Math.max(120, Math.min(words.length, 10) * 26)}>
          <BarChart data={words.slice(0, 10)} layout="vertical" margin={{ left: 8 }}>
            <XAxis type="number" stroke="#94a3b8" fontSize={11} allowDecimals={false} />
            <YAxis type="category" dataKey="name" stroke="#94a3b8" fontSize={11} width={100} />
            <Tooltip contentStyle={tooltipStyle} />
            <Bar dataKey="count" fill="#8B5CF6" radius={[0, 4, 4, 0]} />
          </BarChart>
        </ResponsiveContainer>
      )}
      {(stat.samples?.length || 0) > 0 && (
        <div className="mt-3 space-y-1.5">
          <div className="text-xs text-slate-500 uppercase">Sample answers</div>
          {stat.samples!.map((s, i) => <div key={i} className="text-sm text-slate-300 bg-slate-900/50 rounded-lg px-3 py-1.5 truncate" title={s}>“{s}”</div>)}
        </div>
      )}
      {words.length === 0 && (stat.samples?.length || 0) === 0 && <Empty />}
    </Card>
  );
};

const Empty: React.FC = () => <div className="text-slate-500 text-sm py-8 text-center italic">No data in this segment.</div>;

// ── Cross-tab + significance tests ───────────────────────────────────────────
const CrossTabPanel: React.FC<{ surveyId: string; questions: SurveyQuestionDraft[]; configById: Map<string, any>; filter: SegmentFilter | null }> = ({ surveyId, questions, configById, filter }) => {
  const [q1, setQ1] = useState('');
  const [q2, setQ2] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [result, setResult] = useState<any>(null);

  const qOf = (qid: string) => questions.find((q) => q.id === qid);
  const isNum = (qid: string) => qOf(qid)?.question_type === 'RATING';

  const run = async () => {
    if (!q1 || !q2 || q1 === q2) { setErr('Pick two different questions.'); return; }
    setBusy(true); setErr(null); setResult(null);
    try {
      if (isNum(q1) && isNum(q2)) {
        const pairs: NumericPairs = await getNumericPairs(surveyId, q1, q2, filter);
        const r = pearsonFromSums(pairs);
        setResult({ kind: 'numeric', pairs, r });
      } else {
        const cells = await getCrosstab(surveyId, q1, q2, filter);
        const piv = pivotCrosstab(cells);
        const test = chiSquareTest(piv.matrix);
        setResult({ kind: 'cat', piv, test });
      }
    } catch (e: any) { setErr(e.message || 'Could not run the analysis.'); }
    finally { setBusy(false); }
  };

  const c1 = configById.get(q1), c2 = configById.get(q2);

  return (
    <Card title="Cross-tabulation & significance tests" subtitle="Compare any two questions. Categorical → chi-square; numeric (ratings) → Pearson correlation.">
      <div className="flex flex-wrap items-center gap-2 mb-4">
        <QSelect value={q1} onChange={setQ1} questions={questions} placeholder="Question A" />
        <span className="text-slate-500">×</span>
        <QSelect value={q2} onChange={setQ2} questions={questions} placeholder="Question B" />
        <button onClick={run} disabled={busy} className="bg-indigo-600 hover:bg-indigo-500 text-white px-4 py-1.5 rounded-lg text-sm font-medium flex items-center gap-1.5 disabled:opacity-50">
          {busy ? <Loader2 size={15} className="animate-spin" /> : <TableProperties size={15} />} Run
        </button>
      </div>
      {err && <div className="text-red-400 text-sm mb-3">{err}</div>}

      {result?.kind === 'cat' && (
        <CatResult piv={result.piv} test={result.test} c1={c1} c2={c2} />
      )}
      {result?.kind === 'numeric' && (
        <NumResult pairs={result.pairs} r={result.r} labelA={getText(qOf(q1)?.question_text)} labelB={getText(qOf(q2)?.question_text)} />
      )}
    </Card>
  );
};

const QSelect: React.FC<{ value: string; onChange: (v: string) => void; questions: SurveyQuestionDraft[]; placeholder: string }> = ({ value, onChange, questions, placeholder }) => (
  <select value={value} onChange={(e) => onChange(e.target.value)} className="bg-slate-900 border border-slate-600 rounded-lg px-2 py-1.5 text-white text-sm max-w-[12rem] focus:outline-none focus:border-indigo-500">
    <option value="">{placeholder}</option>
    {questions.map((q) => <option key={q.id} value={q.id}>{q.order_index}. {getText(q.question_text)}</option>)}
  </select>
);

const CatResult: React.FC<{ piv: any; test: any; c1: any; c2: any }> = ({ piv, test, c1, c2 }) => {
  const data = piv.rows.map((r: string, i: number) => {
    const o: any = { name: valueLabel(c1, r) };
    piv.cols.forEach((c: string, j: number) => { o[valueLabel(c2, c)] = piv.matrix[i][j]; });
    return o;
  });
  return (
    <div className="space-y-4">
      {test.valid && (
        <div className="bg-slate-900/60 rounded-lg p-3 text-sm flex flex-wrap gap-x-6 gap-y-1">
          <span className="text-slate-300">χ² = <b className="text-white">{test.chi2.toFixed(2)}</b></span>
          <span className="text-slate-300">df = <b className="text-white">{test.df}</b></span>
          <span className="text-slate-300">{significance(test.p)}</span>
          <span className="text-slate-300">Cramér's V = <b className="text-white">{test.cramersV.toFixed(2)}</b> ({effectStrength(test.cramersV)})</span>
        </div>
      )}
      <ResponsiveContainer width="100%" height={Math.max(200, data.length * 38)}>
        <BarChart data={data} layout="vertical" margin={{ left: 8, right: 16 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="#334155" horizontal={false} />
          <XAxis type="number" stroke="#94a3b8" fontSize={11} allowDecimals={false} />
          <YAxis type="category" dataKey="name" stroke="#94a3b8" fontSize={11} width={120} />
          <Tooltip contentStyle={tooltipStyle} />
          <Legend wrapperStyle={{ fontSize: 11 }} />
          {piv.cols.map((c: string, j: number) => <Bar key={c} dataKey={valueLabel(c2, c)} stackId="a" fill={COLORS[j % COLORS.length]} />)}
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
};

const NumResult: React.FC<{ pairs: NumericPairs; r: any; labelA: string; labelB: string }> = ({ pairs, r, labelA, labelB }) => (
  <div className="space-y-4">
    {r.valid ? (
      <div className="bg-slate-900/60 rounded-lg p-3 text-sm flex flex-wrap gap-x-6 gap-y-1">
        <span className="text-slate-300">Pearson r = <b className="text-white">{r.r.toFixed(3)}</b> ({effectStrength(r.r)}{r.r < 0 ? ', negative' : ''})</span>
        <span className="text-slate-300">n = <b className="text-white">{r.n}</b></span>
        <span className="text-slate-300">{significance(r.p)}</span>
      </div>
    ) : <div className="text-slate-400 text-sm">Not enough paired numeric data.</div>}
    {pairs.grid?.length > 0 && (
      <ResponsiveContainer width="100%" height={300}>
        <ScatterChart margin={{ left: 8, bottom: 12 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="#334155" />
          <XAxis type="number" dataKey="x" name={labelA} stroke="#94a3b8" fontSize={11} label={{ value: labelA, position: 'insideBottom', offset: -8, fill: '#94a3b8', fontSize: 11 }} />
          <YAxis type="number" dataKey="y" name={labelB} stroke="#94a3b8" fontSize={11} />
          <ZAxis type="number" dataKey="n" range={[30, 400]} name="count" />
          <Tooltip contentStyle={tooltipStyle} cursor={{ strokeDasharray: '3 3' }} />
          <Scatter data={pairs.grid} fill="#6366F1" fillOpacity={0.6} />
        </ScatterChart>
      </ResponsiveContainer>
    )}
  </div>
);

// ── AI insights ──────────────────────────────────────────────────────────────
const AiInsights: React.FC<{ surveyId: string }> = ({ surveyId }) => {
  const [busy, setBusy] = useState(false);
  const [text, setText] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);

  const run = async () => {
    setBusy(true); setErr(null);
    try {
      const res = await analyzeSurveys(
        'Summarize the key findings, notable patterns, segments worth a closer look, and any data-quality caveats for this survey. Be concise and specific.',
        [surveyId]
      );
      const insights = (res.key_insights || []).map((k) => `- ${k}`).join('\n');
      setText([res.summary, insights].filter(Boolean).join('\n\n') || 'No summary returned.');
    } catch (e: any) { setErr(e.message || 'AI analysis failed.'); }
    finally { setBusy(false); }
  };

  return (
    <Card title="AI insights" subtitle="A written summary of what stands out in this survey.">
      {!text && (
        <button onClick={run} disabled={busy} className="bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-500 hover:to-purple-500 text-white px-4 py-2 rounded-lg text-sm font-medium flex items-center gap-2 disabled:opacity-50">
          {busy ? <Loader2 size={15} className="animate-spin" /> : <Sparkles size={15} />} Generate AI insights
        </button>
      )}
      {err && <div className="text-red-400 text-sm mt-2">{err}</div>}
      {text && (
        <div className="prose prose-invert prose-sm max-w-none text-slate-300">
          <ReactMarkdown>{text}</ReactMarkdown>
          <button onClick={run} disabled={busy} className="not-prose mt-2 text-xs text-indigo-400 hover:text-indigo-300 flex items-center gap-1">
            {busy ? <Loader2 size={12} className="animate-spin" /> : <Sparkles size={12} />} Regenerate
          </button>
        </div>
      )}
    </Card>
  );
};

// ── JSON export button ───────────────────────────────────────────────────────
const JsonButton: React.FC<{ surveyId: string }> = ({ surveyId }) => {
  const [busy, setBusy] = useState(false);
  const download = async () => {
    setBusy(true);
    try {
      const data = await getDashboardJson(surveyId);
      const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url; a.download = `dashboard_${surveyId}.json`;
      document.body.appendChild(a); a.click(); document.body.removeChild(a); URL.revokeObjectURL(url);
    } catch (e: any) { alert(e.message || 'JSON export failed.'); }
    finally { setBusy(false); }
  };
  return (
    <button onClick={download} disabled={busy} title="Download dashboard as JSON"
      className="px-3 py-1.5 rounded-lg text-sm font-medium bg-slate-800 hover:bg-slate-700 border border-slate-700 text-slate-300 flex items-center gap-1.5 disabled:opacity-50">
      {busy ? <Loader2 size={15} className="animate-spin" /> : <Code2 size={15} />} JSON
    </button>
  );
};

// ── Release panel (owner) ────────────────────────────────────────────────────
const ReleasePanel: React.FC<{ offer: DashboardOffer; onChanged: () => void }> = ({ offer, onChanged }) => {
  const [access, setAccess] = useState<'FREE' | 'PAID'>(offer.access || 'FREE');
  const [formats, setFormats] = useState<ReleaseFormat[]>(offer.formats?.length ? offer.formats : ['VISUAL']);
  const [price, setPrice] = useState<number | null>(offer.price ?? null);
  const [currency, setCurrency] = useState<'USD' | 'KHR'>(offer.currency === 'KHR' ? 'KHR' : 'USD');
  const [methods, setMethods] = useState<('KHQR' | 'X402')[]>((offer.methods as any) || []);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  const toggle = <T,>(arr: T[], v: T): T[] => (arr.includes(v) ? arr.filter((x) => x !== v) : [...arr, v]);

  const publish = async () => {
    setErr(null);
    if (formats.length === 0) return setErr('Pick at least one format (Visual or JSON).');
    if (access === 'PAID') {
      if (!price || price <= 0) return setErr('Set a price for a paid release.');
      if (methods.length === 0) return setErr('Pick at least one payment method.');
      if (methods.includes('X402') && currency !== 'USD') return setErr('x402 (USDC) supports USD pricing only.');
    }
    setBusy(true);
    try {
      const cfg: PublishConfig = { access, formats, price, currency, methods };
      const res = await publishDashboard(offer.survey_id, cfg);
      if (res && res.ok === false) {
        setErr(res.reason === 'INSUFFICIENT_CREDITS'
          ? `Not enough credits to publish (need ${res.needed}, have ${res.available}). Top up in Settings.`
          : 'Could not publish.');
        return;
      }
      onChanged();
    } catch (e: any) { setErr(e.message || 'Could not publish.'); }
    finally { setBusy(false); }
  };

  const unpublish = async () => {
    if (!window.confirm('Take this dashboard offline? Buyers keep their access; no credit refund.')) return;
    setBusy(true);
    try { await unpublishDashboard(offer.survey_id); onChanged(); }
    catch (e: any) { setErr(e.message || 'Could not unpublish.'); }
    finally { setBusy(false); }
  };

  const copyLink = () => { navigator.clipboard?.writeText(window.location.href); setCopied(true); setTimeout(() => setCopied(false), 1800); };

  if (offer.published) {
    return (
      <div className="bg-slate-800 border border-emerald-600/30 rounded-xl p-4 flex flex-wrap items-center gap-3">
        <span className="flex items-center gap-2 text-emerald-300 font-semibold text-sm"><Rocket size={16} /> Released</span>
        <span className={`text-xs px-2 py-0.5 rounded-full ${offer.access === 'PAID' ? 'bg-indigo-500/15 text-indigo-300' : 'bg-emerald-500/15 text-emerald-300'}`}>
          {offer.access === 'PAID' ? <>{offer.price} {offer.currency} · {(offer.methods || []).join(', ')}</> : 'Free'}
        </span>
        <span className="text-xs text-slate-400 flex items-center gap-1">{offer.formats?.includes('VISUAL') && <><BarChart2 size={12} /> Visual</>} {offer.formats?.includes('JSON') && <><Code2 size={12} /> JSON</>}</span>
        <div className="ml-auto flex items-center gap-2">
          <button onClick={copyLink} className="text-xs text-slate-300 hover:text-white flex items-center gap-1"><LinkIcon size={13} /> {copied ? 'Copied!' : 'Copy link'}</button>
          <button onClick={unpublish} disabled={busy} className="text-xs text-red-300 hover:text-red-200">Unpublish</button>
        </div>
        {err && <p className="w-full text-red-400 text-xs">{err}</p>}
      </div>
    );
  }

  return (
    <div className="bg-slate-800 border border-slate-700 rounded-xl p-5 space-y-4">
      <div>
        <h3 className="text-white font-semibold flex items-center gap-2"><Rocket size={16} className="text-indigo-400" /> Release this dashboard</h3>
        <p className="text-slate-400 text-sm mt-0.5">Publishing costs <b className="text-slate-200">50 credits</b>. Share it free, or charge buyers (paid directly to you).</p>
      </div>

      <div className="flex flex-wrap gap-2">
        <button onClick={() => setAccess('FREE')} className={`px-4 py-2 rounded-lg text-sm font-medium border flex items-center gap-2 ${access === 'FREE' ? 'bg-emerald-600 border-emerald-400 text-white' : 'bg-slate-900 border-slate-600 text-slate-300'}`}><Globe size={15} /> Free</button>
        <button onClick={() => setAccess('PAID')} className={`px-4 py-2 rounded-lg text-sm font-medium border flex items-center gap-2 ${access === 'PAID' ? 'bg-indigo-600 border-indigo-400 text-white' : 'bg-slate-900 border-slate-600 text-slate-300'}`}><Tag size={15} /> Charge</button>
      </div>

      <div>
        <label className="block text-xs text-slate-400 mb-1.5">Formats</label>
        <div className="flex gap-2">
          {(['VISUAL', 'JSON'] as ReleaseFormat[]).map((f) => (
            <button key={f} onClick={() => setFormats((p) => toggle(p, f))}
              className={`px-4 py-2 rounded-lg text-sm font-medium border flex items-center gap-2 ${formats.includes(f) ? 'bg-slate-700 border-slate-500 text-white' : 'bg-slate-900 border-slate-600 text-slate-400'}`}>
              {f === 'VISUAL' ? <BarChart2 size={15} /> : <Code2 size={15} />} {f === 'VISUAL' ? 'Visual dashboard' : 'JSON export'}
            </button>
          ))}
        </div>
      </div>

      {access === 'PAID' && (
        <div className="space-y-3 pl-1 border-l-2 border-indigo-500/30">
          <div className="flex flex-wrap items-end gap-3 pl-3">
            <div>
              <label className="block text-xs text-slate-400 mb-1">Price</label>
              <input type="number" min={0} step="0.01" value={price ?? ''} onChange={(e) => setPrice(e.target.value === '' ? null : parseFloat(e.target.value))}
                className="w-28 bg-slate-900 border border-slate-600 rounded-lg px-3 py-2 text-white focus:outline-none focus:border-indigo-500" placeholder="5.00" />
            </div>
            <div>
              <label className="block text-xs text-slate-400 mb-1">Currency</label>
              <select value={currency} onChange={(e) => setCurrency(e.target.value as any)} className="bg-slate-900 border border-slate-600 rounded-lg px-3 py-2 text-white focus:outline-none focus:border-indigo-500">
                <option value="USD">USD</option><option value="KHR">KHR</option>
              </select>
            </div>
            <div className="flex gap-2">
              {(['KHQR', 'X402'] as const).map((m) => (
                <button key={m} onClick={() => setMethods((p) => toggle(p, m))}
                  className={`px-3 py-2 rounded-lg text-sm font-medium border ${methods.includes(m) ? 'bg-indigo-600 border-indigo-400 text-white' : 'bg-slate-900 border-slate-600 text-slate-300'}`}>
                  {m === 'KHQR' ? 'KHQR' : 'x402'}
                </button>
              ))}
            </div>
          </div>
          <p className="text-xs text-slate-500 pl-3">Set payout details in <a href="#/settings" className="text-indigo-400 hover:underline">Settings → Payout details</a>. x402 needs USD.</p>
        </div>
      )}

      {err && <p className="text-red-400 text-sm">{err}</p>}
      <button onClick={publish} disabled={busy} className="bg-indigo-600 hover:bg-indigo-500 text-white px-5 py-2.5 rounded-lg font-bold flex items-center gap-2 disabled:opacity-50">
        {busy ? <Loader2 size={16} className="animate-spin" /> : <Rocket size={16} />} Publish (50 credits)
      </button>
    </div>
  );
};

// ── Paywall (buyer) ──────────────────────────────────────────────────────────
const Paywall: React.FC<{ offer: DashboardOffer; onUnlocked: () => void }> = ({ offer, onUnlocked }) => {
  const [method, setMethod] = useState<'KHQR' | 'X402' | null>(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [qr, setQr] = useState<string | null>(null);
  const [waiting, setWaiting] = useState(false);
  const price = `${offer.price} ${offer.currency}`;

  const startKhqr = async () => {
    setBusy(true); setErr(null);
    try {
      await startKhqrPurchase(offer.survey_id);
      if (!offer.khqr) throw new Error('Seller has not configured KHQR.');
      const payload = buildKhqr({
        account: offer.khqr.account, merchantName: offer.khqr.merchant, city: offer.khqr.city,
        amount: offer.price || undefined, currency: offer.currency === 'KHR' ? 'KHR' : 'USD',
      });
      const QR: any = await import('qrcode');
      setQr(await QR.toDataURL(payload, { width: 280, margin: 1 }));
      setMethod('KHQR'); setWaiting(true);
    } catch (e: any) { setErr(e.message || 'Could not start KHQR payment.'); }
    finally { setBusy(false); }
  };

  const payX402 = async () => {
    setBusy(true); setErr(null); setMethod('X402');
    try { await payDashboardWithX402(offer.survey_id); onUnlocked(); }
    catch (e: any) { setErr(e.message || 'x402 payment failed.'); }
    finally { setBusy(false); }
  };

  return (
    <div className="max-w-lg mx-auto bg-slate-800 border border-slate-700 rounded-2xl p-8 mt-8">
      <div className="text-center mb-6">
        <div className="w-14 h-14 rounded-2xl bg-indigo-500/15 border border-indigo-500/30 flex items-center justify-center mx-auto mb-3"><Lock size={26} className="text-indigo-400" /></div>
        <h2 className="text-xl font-bold text-white">{getText(offer.title)}</h2>
        <p className="text-slate-400 text-sm mt-1">Unlock this survey's full analytics dashboard.</p>
        <div className="text-3xl font-black text-white mt-4">{price}</div>
        <p className="text-[11px] text-slate-500">one-time · perpetual access · aggregated data only</p>
      </div>

      {err && <div className="bg-red-900/20 border border-red-500/30 p-3 rounded-lg text-red-200 text-sm mb-4">{err}</div>}

      {qr ? (
        <div className="text-center space-y-3">
          <img src={qr} alt="KHQR" className="mx-auto rounded-xl bg-white p-3" />
          <p className="text-slate-300 text-sm">Scan with any Bakong-enabled app to pay <b>{price}</b>.</p>
          <p className="text-slate-500 text-xs">Access unlocks once the seller confirms your payment.</p>
          <button onClick={onUnlocked} disabled={busy} className="w-full bg-indigo-600 hover:bg-indigo-500 text-white font-medium py-2.5 rounded-lg flex items-center justify-center gap-2">
            {busy ? <Loader2 size={16} className="animate-spin" /> : <CheckCircle2 size={16} />} I've paid — check access
          </button>
        </div>
      ) : (
        <div className="space-y-2.5">
          {offer.methods.includes('KHQR') && (
            <button onClick={startKhqr} disabled={busy || !offer.khqr}
              className="w-full flex items-center justify-between gap-3 bg-slate-900 hover:bg-slate-700 border border-slate-600 rounded-xl px-4 py-3 text-left disabled:opacity-50">
              <span className="flex items-center gap-3 text-white font-medium"><QrCode size={20} className="text-indigo-400" /> Pay with KHQR</span>
              <span className="text-xs text-slate-500">Bakong</span>
            </button>
          )}
          {offer.methods.includes('X402') && (
            <button onClick={payX402} disabled={busy || !offer.x402_wallet}
              className="w-full flex items-center justify-between gap-3 bg-slate-900 hover:bg-slate-700 border border-slate-600 rounded-xl px-4 py-3 text-left disabled:opacity-50">
              <span className="flex items-center gap-3 text-white font-medium">
                {busy && method === 'X402' ? <Loader2 size={20} className="animate-spin text-indigo-400" /> : <Wallet size={20} className="text-indigo-400" />} Pay with x402
              </span>
              <span className="text-xs text-slate-500">{hasWallet() ? 'USDC · Base' : 'wallet needed'}</span>
            </button>
          )}
          {offer.methods.length === 0 && <p className="text-slate-500 text-sm text-center">The seller hasn't enabled a payment method yet.</p>}
        </div>
      )}
    </div>
  );
};

// ── Owner sales panel ────────────────────────────────────────────────────────
const OwnerSales: React.FC<{ surveyId: string }> = ({ surveyId }) => {
  const [sales, setSales] = useState<DashboardPurchase[]>([]);
  const [loading, setLoading] = useState(true);
  const refresh = async () => { try { setSales(await listDashboardSales(surveyId)); } finally { setLoading(false); } };
  useEffect(() => { refresh(); /* eslint-disable-next-line */ }, [surveyId]);

  const confirm = async (p: DashboardPurchase) => {
    const ref = window.prompt('Payment reference (optional) — confirm you received this KHQR payment:') ?? '';
    try { await confirmDashboardPurchase(p.id, ref); await refresh(); } catch (e: any) { alert(e.message || 'Could not confirm.'); }
  };

  const paid = sales.filter((s) => s.status === 'PAID');
  const pending = sales.filter((s) => s.status === 'PENDING_PAYMENT');
  const revenue = paid.reduce((sum, s) => sum + (Number(s.amount) || 0), 0);

  return (
    <Card title="Dashboard sales" subtitle="Buyers who purchased access. Confirm KHQR payments to grant access.">
      <div className="grid grid-cols-3 gap-3 mb-4 text-center">
        <div className="bg-slate-900/50 rounded-lg py-2"><div className="text-[10px] uppercase text-slate-500">Sales</div><div className="text-lg font-bold text-white">{paid.length}</div></div>
        <div className="bg-slate-900/50 rounded-lg py-2"><div className="text-[10px] uppercase text-slate-500">Revenue</div><div className="text-lg font-bold text-emerald-400">{revenue.toFixed(2)}</div></div>
        <div className="bg-slate-900/50 rounded-lg py-2"><div className="text-[10px] uppercase text-slate-500">Pending</div><div className="text-lg font-bold text-yellow-400">{pending.length}</div></div>
      </div>
      {loading ? <Loader2 className="animate-spin text-indigo-400 mx-auto" size={20} /> : sales.length === 0 ? (
        <p className="text-slate-500 text-sm text-center py-2">No purchases yet.</p>
      ) : (
        <div className="space-y-1.5">
          {sales.map((s) => (
            <div key={s.id} className="flex items-center justify-between bg-slate-900/40 rounded-lg px-3 py-2 text-sm">
              <span className="flex items-center gap-2 text-slate-300">
                {s.method === 'X402' ? <Wallet size={14} className="text-indigo-400" /> : <QrCode size={14} className="text-indigo-400" />}
                {s.method} · {s.amount} {s.currency}
              </span>
              {s.status === 'PAID' ? (
                <span className="text-emerald-400 flex items-center gap-1 text-xs"><Check size={13} /> Paid</span>
              ) : s.status === 'PENDING_PAYMENT' ? (
                <button onClick={() => confirm(s)} className="text-xs bg-indigo-600 hover:bg-indigo-500 text-white px-2.5 py-1 rounded-md flex items-center gap-1"><Coins size={12} /> Confirm received</button>
              ) : <span className="text-slate-500 text-xs">Cancelled</span>}
            </div>
          ))}
        </div>
      )}
    </Card>
  );
};

export default SurveyDashboardView;
